import React from 'react';
import { Box, Grid, useColorModeValue } from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { matchPath, Outlet, useLocation } from 'react-router-dom';

import { BarcodeScannerOverlay } from 'clipsal-cortex-ui/src/components/BarcodeScannerOverlay';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import packageJson from '../package.json';
import { Banner } from './common/components/Banner';
import { COMPONENT_MIN_HEIGHT, IS_PRODUCTION_DATA } from './common/constants';
import { useAppReady } from './common/hooks/use-app-ready';
import { useAppUpdate } from './common/hooks/use-app-update';
import { useAuthRedirect } from './common/hooks/use-auth-redirect';
import { useHighchartSetup } from './common/hooks/use-highchart-setup';
import { usePushNotificationSetup } from './common/hooks/use-push-notification-setup';
import { useStatusBarStyleConfiguration } from './common/hooks/use-status-bar-style-configuration';
import { useViewScrollTop } from './common/hooks/use-view-scroll-top';
import { FeatureFlagsModal } from './features/account/FeatureFlagsModal';
import { selectEvseType } from './features/ev-charger/dashboard/evChargerSlice';
import { selectSite } from './features/site/siteSlice';
import { selectUser } from './features/user/userSlice';

export interface LinkData {
  component: React.JSX.Element;
  path: string;
  requiresLogin: boolean;
}

export function App() {
  const { t } = useTranslation();
  const backgroundColor = useColorModeValue('#ffffff', '#111111');
  const user = useSelector(selectUser);
  const evseType = useSelector(selectEvseType);
  const { site_id: siteId } = useSelector(selectSite);
  const { pathname } = useLocation();
  useHighchartSetup();
  useStatusBarStyleConfiguration();
  useViewScrollTop();
  usePushNotificationSetup();
  useAppUpdate();
  useAuthRedirect();
  const isLoaded = useAppReady();

  const showStagingBanner = user.isLoggedIn && !IS_PRODUCTION_DATA && pathname !== '/login';
  const siteRoute = matchPath('/sites/:id/*', pathname);
  const showDeviceVariant = !!siteId && siteRoute?.params.id === String(siteId);

  return (
    <Box background={backgroundColor}>
      <Box
        style={{ backdropFilter: 'blur(6px)' }}
        width="100vw"
        background={backgroundColor}
        zIndex={20}
        position="fixed"
        height={'env(safe-area-inset-top)'}
        top="0px"
      />

      {showStagingBanner && (
        <Banner
          text={
            <>
              <Trans t={t} i18nKey="Staging Mode.message" values={{ version: packageJson.version }} />
              {showDeviceVariant && <strong>{` - ${evseType}`}</strong>}
            </>
          }
          testId="staging-banner"
        />
      )}

      <BarcodeScannerOverlay title={t('Common.scan qr code')} />
      {isLoaded ? (
        <Box>
          <Grid minHeight={COMPONENT_MIN_HEIGHT} height="100%" templateColumns={'1fr'}>
            <Box>
              <Outlet />
            </Box>
          </Grid>
        </Box>
      ) : (
        <Box height={COMPONENT_MIN_HEIGHT}>
          <CenteredLoader text={`${t('Common.loading')}...`} />
        </Box>
      )}
      <FeatureFlagsModal />
    </Box>
  );
}
